// Filtrado, orden y métricas de «Mis ventas» sobre las páginas YA cargadas — lógica PURA, sin React.
// Qué parte de los filtros viaja al servidor lo decide `mySalesServerQuery`.
import { mxDay } from '@/lib/dateHelpers';
import { ticketHeadline } from '@/shared/patterns/SaleTicket/ticketSummary';
import type { RecentSale } from '../../api/usePortal';
import { saleCustomerLabel } from '@/shared/lib/format/saleCustomer';
import { isSearchableCurp } from '../../customers/lib/customerSearchTerm';
import { financierKeyOf } from './mySalesFormat';

/** Los dos chips de Tipo del mockup. «credito» agrupa el crédito propio y la derivación externa. */
export type SaleTypeFilter = 'contado' | 'credito';

export interface MySalesFilters {
    /** Texto libre sobre folio y nombre del cliente; la CURP completa la filtra solo el servidor. */
    query: string;
    /** Clave de `financierKeyOf`: `contado`, `SUREKEEP`, `PAYJOY`… `''` es «todas». */
    financier: string;
    types: SaleTypeFilter[];
    /** Marca del equipo, de las que hay en la tabla. `''` es «todas». */
    brand: string;
    /** Mín y máx en PESOS, tal como se teclean; el precio de la venta viaja en centavos. */
    minAmount: string;
    maxAmount: string;
    /** Rango de venta, en días de calendario `YYYY-MM-DD`. */
    from: string;
    to: string;
}

export const EMPTY_FILTERS: MySalesFilters = {
    query: '',
    financier: '',
    types: [],
    brand: '',
    minAmount: '',
    maxAmount: '',
    from: '',
    to: '',
};

export type SortKey =
    | 'soldAt'
    | 'saleRef'
    | 'customerName'
    | 'product'
    | 'financier'
    | 'amount'
    | 'status';

export type SortDirection = 'asc' | 'desc';

export interface SortState {
    key: SortKey;
    direction: SortDirection;
}

/** Lo más nuevo arriba: la venta que el vendedor acaba de cerrar abre la pantalla. */
export const DEFAULT_SORT: SortState = { key: 'soldAt', direction: 'desc' };

/** Columnas de texto: cambiar a una de ellas arranca A→Z, no de mayor a menor. */
const TEXT_SORT_KEYS: SortKey[] = ['saleRef', 'customerName', 'product', 'financier', 'status'];

/** Los pesos tecleados a número. `null` cuando el campo está vacío o no es un número. */
function parseAmount(value: string): number | null {
    if (value.trim() === '') return null;
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
}

/**
 * En qué chip de Tipo cae una venta. El crédito propio y el derivado son el mismo chip; la venta
 * que llega sin tipo no cae en ninguno, porque el listado se castea sin validar.
 */
function saleKindOf(type: string): SaleTypeFilter | null {
    if (type === 'CASH') return 'contado';
    if (type === 'CREDIT' || type === 'EXTERNAL_REFERRAL') return 'credito';
    return null;
}

function matchesType(sale: RecentSale, types: SaleTypeFilter[]): boolean {
    if (types.length === 0) return true;
    const kind = saleKindOf(sale.type);
    return kind !== null && types.includes(kind);
}

/** Monto de la venta en Mis ventas y el Home: lo vendido (DEC-46), luego precio del equipo y lo cobrado. */
export function saleAmountCents(sale: {
    soldCents?: number | null;
    amountCents?: number | null;
    equipmentPriceCents: number | null;
}): number | null {
    return sale.soldCents ?? sale.equipmentPriceCents ?? sale.amountCents ?? null;
}

/** Filtros en memoria sobre lo cargado. Se componen en AND. */
export function filterSales(rows: RecentSale[], filters: MySalesFilters): RecentSale[] {
    const query = filters.query.trim().toLowerCase();
    const min = parseAmount(filters.minAmount);
    const max = parseAmount(filters.maxAmount);

    return rows.filter((sale) => {
        // La venta ya no trae la CURP: con una CURP completa manda lo que filtró el servidor.
        if (query && !isSearchableCurp(query)) {
            const haystack = `${sale.saleRef} ${saleCustomerLabel(sale) ?? ''}`.toLowerCase();
            if (!haystack.includes(query)) return false;
        }
        if (
            filters.financier &&
            financierKeyOf(sale.type, sale.externalProvider) !== filters.financier
        ) {
            return false;
        }
        if (!matchesType(sale, filters.types)) return false;
        if (filters.brand && sale.productBrand !== filters.brand) return false;

        // Sin monto la venta no se puede ubicar en el rango: queda fuera, no dentro.
        if (min !== null || max !== null) {
            const cents = saleAmountCents(sale);
            if (cents === null) return false;
            const pesos = cents / 100;
            if (min !== null && pesos < min) return false;
            if (max !== null && pesos > max) return false;
        }

        // Por DÍA del piso de venta: el día UTC de una venta de las 22:50 en CDMX es el siguiente.
        if (filters.from || filters.to) {
            const day = mxDay(sale.soldAt);
            if (filters.from && day < filters.from) return false;
            if (filters.to && day > filters.to) return false;
        }
        return true;
    });
}

/** El valor por el que se compara cada columna. El monto ausente se ordena como el más chico. */
function sortValue(sale: RecentSale, key: SortKey): string | number {
    switch (key) {
        case 'soldAt':
            return sale.soldAt;
        case 'saleRef':
            return sale.saleRef;
        case 'customerName':
            return saleCustomerLabel(sale) ?? '';
        case 'product':
            return ticketHeadline(sale, null, sale.product).name;
        case 'financier':
            return financierKeyOf(sale.type, sale.externalProvider) ?? '';
        case 'amount':
            return saleAmountCents(sale) ?? -1;
        case 'status':
            return sale.status;
    }
}

export function sortSales(rows: RecentSale[], sort: SortState): RecentSale[] {
    const sign = sort.direction === 'asc' ? 1 : -1;
    return [...rows].sort((a, b) => {
        const va = sortValue(a, sort.key);
        const vb = sortValue(b, sort.key);
        if (typeof va === 'string' && typeof vb === 'string') {
            return sign * va.localeCompare(vb, 'es-MX');
        }
        return sign * (Number(va) - Number(vb));
    });
}

/** Repetir columna invierte; cambiar de columna arranca A→Z en texto y de mayor a menor en el resto. */
export function nextSort(current: SortState, key: SortKey): SortState {
    if (current.key === key) {
        return { key, direction: current.direction === 'asc' ? 'desc' : 'asc' };
    }
    return { key, direction: TEXT_SORT_KEYS.includes(key) ? 'asc' : 'desc' };
}

/** Las cinco píldoras de la barra de arriba de la tabla. */
export type SalesSegment = 'all' | 'today' | 'surekeep' | 'cash' | 'others';

export type SegmentCounts = Record<SalesSegment, number>;

/** Si la venta cae en el recorte. La venta sin financiera determinable solo cae en «Todas». */
function inSegment(sale: RecentSale, segment: SalesSegment): boolean {
    if (segment === 'all') return true;
    if (segment === 'today') return sale.today;
    const key = financierKeyOf(sale.type, sale.externalProvider);
    if (key === null) return false;
    if (segment === 'surekeep') return key === 'SUREKEEP';
    if (segment === 'cash') return key === 'contado';
    return key !== 'SUREKEEP' && key !== 'contado';
}

/** El recorte de la píldora elegida, encima de los filtros del panel. */
export function filterBySegment(rows: RecentSale[], segment: SalesSegment): RecentSale[] {
    return segment === 'all' ? rows : rows.filter((sale) => inSegment(sale, segment));
}

/** Los contadores de las píldoras, sobre las filas que ya pasaron los filtros del panel. */
export function segmentCounts(rows: RecentSale[]): SegmentCounts {
    const count = (segment: SalesSegment) => rows.filter((sale) => inSegment(sale, segment)).length;
    return {
        all: rows.length,
        today: count('today'),
        surekeep: count('surekeep'),
        cash: count('cash'),
        others: count('others'),
    };
}

/** Cuántos filtros hay puestos — el contador del encabezado del panel. */
export function countActiveFilters(filters: MySalesFilters): number {
    const fields = [
        filters.query,
        filters.financier,
        filters.brand,
        filters.minAmount,
        filters.maxAmount,
        filters.from,
        filters.to,
    ].filter((value) => value !== '').length;
    return fields + filters.types.length;
}

/** Agrega o quita un valor de un grupo de chips multi-selección. */
export function toggleInList<T>(list: T[], value: T): T[] {
    return list.includes(value) ? list.filter((v) => v !== value) : [...list, value];
}

/**
 * Las marcas del `<select>` de Producto. Salen de las ventas cargadas, no del catálogo: el filtro
 * tiene que ofrecer lo que hay en la tabla, o se elige una marca que no devuelve nada.
 */
export function brandOptions(rows: RecentSale[]): string[] {
    const brands = new Set<string>();
    for (const sale of rows) {
        if (sale.productBrand) brands.add(sale.productBrand);
    }
    return [...brands].sort((a, b) => a.localeCompare(b, 'es-MX'));
}

/** Las tres métricas del toolbar. En `null` lo que no hay con qué calcular — nunca en cero. */
export interface SalesStats {
    /** Ventas NO canceladas del resultado filtrado: el denominador de todas las métricas. */
    activeCount: number;
    volumeCents: number | null;
    avgTicketCents: number | null;
    surekeepPercent: number | null;
    /** Ventas activas sin monto vendido, precio de equipo ni cobrado, fuera del volumen. */
    missingPriceCount: number;
}

/**
 * Las métricas del toolbar, sobre las ventas no canceladas — igual que el `renderStats()` del
 * mockup. La venta sin monto NO se suma como 0: se excluye y se cuenta aparte, para que
 * el pie de la tabla pueda decir cuántas quedaron fuera.
 */
export function salesStats(rows: RecentSale[]): SalesStats {
    const active = rows.filter((sale) => sale.status !== 'CANCELLED');
    const amounts = active.map(saleAmountCents).filter((cents): cents is number => cents !== null);
    const volumeCents = amounts.reduce((total, cents) => total + cents, 0);
    const surekeepCount = active.filter(
        (sale) => financierKeyOf(sale.type, sale.externalProvider) === 'SUREKEEP',
    ).length;

    return {
        activeCount: active.length,
        volumeCents: amounts.length > 0 ? volumeCents : null,
        avgTicketCents: amounts.length > 0 ? Math.round(volumeCents / amounts.length) : null,
        surekeepPercent:
            active.length > 0 ? Math.round((surekeepCount / active.length) * 100) : null,
        missingPriceCount: active.length - amounts.length,
    };
}
